import React, { useEffect, useMemo, useState } from "react";
import {
  ReloadOutlined,
  SearchOutlined,
  MailOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import "./PartnerLeadAdmin.css";

const API_URL = process.env.REACT_APP_API || "http://localhost:4000/api";

const STATUS_LABEL = {
  pending: "ยังไม่ส่ง",
  sent: "ส่งแล้ว",
  failed: "ส่งไม่สำเร็จ",
  converted: "สมัครแล้ว",
};

const STATUS_OPTIONS = ["all", "pending", "sent", "failed", "converted"];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const initialForm = { email: "", name: "", organization: "", note: "" };

// อ่าน response ให้ปลอดภัย: ถ้าไม่ใช่ JSON (เช่น server ตอบ HTML/text ตอน error)
// จะไม่ throw SyntaxError แต่คืน object error message ที่อ่านได้แทน
const safeJson = async (res) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { error: text || `เซิร์ฟเวอร์ตอบกลับไม่ถูกต้อง (status ${res.status})` };
  }
};

const PartnerLeadAdmin = () => {
  const { user } = useSelector((state) => ({ ...state }));
  const authHeaders = user?.token ? { authtoken: user.token } : {};

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);

  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [emailValid, setEmailValid] = useState(null); // null | true | false

  const [sendingId, setSendingId] = useState(null); // id ที่กำลังส่งอีเมลอยู่
  const [sendingBulk, setSendingBulk] = useState(false);

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/partner-lead`, {
        headers: authHeaders,
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || "โหลดข้อมูลไม่สำเร็จ");
      setLeads(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  // ===== ฟอร์มเพิ่ม lead =====
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "email") setEmailValid(null);
  };

  const handleVerifyEmail = async () => {
    const email = form.email.trim();
    if (!email) return;

    setVerifying(true);
    setEmailValid(null);
    try {
      const res = await fetch(
        `${API_URL}/partner-lead/verify-email?email=${encodeURIComponent(
          email
        )}`,
        { headers: authHeaders }
      );
      const data = await safeJson(res);
      setEmailValid(!!data.valid);
      if (!data.valid) {
        toast.error(data.reason || "ไม่พบโดเมนอีเมลนี้");
      }
    } catch (err) {
      console.error(err);
      setEmailValid(null);
    } finally {
      setVerifying(false);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.email.trim()) {
      setFormError("กรุณากรอกอีเมล");
      return;
    }

    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/partner-lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(form),
      });

      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || "เพิ่มไม่สำเร็จ");

      toast.success("เพิ่มอีเมลสำเร็จ");
      setForm(initialForm);
      setEmailValid(null);
      fetchLeads();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setAdding(false);
    }
  };

  // ===== ส่งอีเมลเชิญ =====
  const handleSendOne = async (id) => {
    setSendingId(id);
    try {
      const res = await fetch(`${API_URL}/partner-lead/${id}/send`, {
        method: "POST",
        headers: authHeaders,
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || "ส่งไม่สำเร็จ");

      toast.success(`ส่งอีเมลถึง ${data.lead.email} สำเร็จ`);
      fetchLeads();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSendBulk = async () => {
    if (selectedIds.length === 0) {
      toast.error("กรุณาเลือก lead อย่างน้อย 1 รายการ");
      return;
    }

    setSendingBulk(true);
    try {
      const res = await fetch(`${API_URL}/partner-lead/send-bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || "ส่งไม่สำเร็จ");

      const successCount = data.results.filter((r) => r.success).length;
      toast.success(`ส่งสำเร็จ ${successCount}/${data.results.length} รายการ`);
      setSelectedIds([]);
      fetchLeads();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingBulk(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("ยืนยันลบ lead รายการนี้?")) return;

    try {
      const res = await fetch(`${API_URL}/partner-lead/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("ลบไม่สำเร็จ");

      toast.success("ลบสำเร็จ");
      setLeads((prev) => prev.filter((l) => l._id !== id));
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ===== filter =====
  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      const matchStatus = statusFilter === "all" || l.status === statusFilter;
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        l.email?.toLowerCase().includes(q) ||
        l.name?.toLowerCase().includes(q) ||
        l.organization?.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [leads, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: leads.length };
    for (const l of leads) counts[l.status] = (counts[l.status] || 0) + 1;
    return counts;
  }, [leads]);

  return (
    <div className="lead-admin-page">
      <div className="lead-admin-header">
        <div>
          <h2 className="lead-admin-title">รายชื่ออีเมลเชิญ Partner</h2>
          <p className="lead-admin-subtitle">ทั้งหมด {leads.length} รายการ</p>
        </div>
        <button
          className="lead-admin-refresh-btn"
          onClick={fetchLeads}
          disabled={loading}
        >
          <ReloadOutlined spin={loading} /> รีเฟรช
        </button>
      </div>

      {/* ===== ฟอร์มเพิ่ม lead ===== */}
      <form className="lead-add-form" onSubmit={handleAddLead}>
        <div className="lead-add-form-row">
          <div className="lead-add-field lead-add-field-email">
            <input
              type="email"
              name="email"
              placeholder="อีเมลที่ต้องการเชิญ"
              value={form.email}
              onChange={handleFormChange}
              onBlur={handleVerifyEmail}
            />
            {verifying && (
              <span className="verify-badge checking">กำลังเช็ค...</span>
            )}
            {!verifying && emailValid === true && (
              <span className="verify-badge valid">
                <CheckCircleOutlined /> โดเมนถูกต้อง
              </span>
            )}
            {!verifying && emailValid === false && (
              <span className="verify-badge invalid">
                <CloseCircleOutlined /> ไม่พบโดเมนนี้
              </span>
            )}
          </div>
          <input
            type="text"
            name="name"
            placeholder="ชื่อ (ไม่บังคับ)"
            value={form.name}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="organization"
            placeholder="องค์กร (ไม่บังคับ)"
            value={form.organization}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="note"
            placeholder="โน้ต (ไม่บังคับ)"
            value={form.note}
            onChange={handleFormChange}
          />
          <button type="submit" disabled={adding} className="lead-add-btn">
            {adding ? "กำลังเพิ่ม..." : "+ เพิ่ม"}
          </button>
        </div>
        {formError && <p className="lead-form-error">{formError}</p>}
      </form>

      {/* ===== Filter + search + bulk send ===== */}
      <div className="lead-admin-toolbar">
        <div className="lead-admin-filters">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              className={`lead-filter-chip ${
                statusFilter === s ? "active" : ""
              }`}
              onClick={() => setStatusFilter(s)}
              type="button"
            >
              {s === "all" ? "ทั้งหมด" : STATUS_LABEL[s]}
              <span className="chip-count">{statusCounts[s] || 0}</span>
            </button>
          ))}
        </div>

        <div className="lead-admin-search">
          <SearchOutlined />
          <input
            type="text"
            placeholder="ค้นหาอีเมล, ชื่อ, องค์กร..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          className="lead-bulk-send-btn"
          onClick={handleSendBulk}
          disabled={sendingBulk || selectedIds.length === 0}
        >
          {sendingBulk ? (
            <>
              <LoadingOutlined spin /> กำลังส่ง...
            </>
          ) : (
            <>
              <MailOutlined /> ส่งที่เลือก ({selectedIds.length})
            </>
          )}
        </button>
      </div>

      {loading && <div className="lead-admin-state">กำลังโหลดข้อมูล...</div>}

      {!loading && error && (
        <div className="lead-admin-state error">
          ❌ {error}
          <button onClick={fetchLeads}>ลองใหม่</button>
        </div>
      )}

      {!loading && !error && filteredLeads.length === 0 && (
        <div className="lead-admin-state">ไม่พบข้อมูล</div>
      )}

      {!loading && !error && filteredLeads.length > 0 && (
        <div className="lead-admin-table-wrap">
          <table className="lead-admin-table">
            <thead>
              <tr>
                <th></th>
                <th>อีเมล</th>
                <th>ชื่อ / องค์กร</th>
                <th>สถานะ</th>
                <th>ส่งแล้ว</th>
                <th>ส่งล่าสุด</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => {
                const isSendingThisRow = sendingId === lead._id;
                return (
                  <tr
                    key={lead._id}
                    className={isSendingThisRow ? "lead-row-sending" : ""}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(lead._id)}
                        onChange={() => toggleSelect(lead._id)}
                        disabled={isSendingThisRow}
                      />
                    </td>
                    <td>{lead.email}</td>
                    <td>
                      {lead.name || "-"}
                      {lead.organization ? ` / ${lead.organization}` : ""}
                    </td>
                    <td>
                      {isSendingThisRow ? (
                        <span className="lead-status-badge status-sending">
                          <LoadingOutlined spin /> กำลังส่ง...
                        </span>
                      ) : (
                        <span
                          className={`lead-status-badge status-${lead.status}`}
                        >
                          {STATUS_LABEL[lead.status] || lead.status}
                        </span>
                      )}
                    </td>
                    <td>{lead.sentCount || 0} ครั้ง</td>
                    <td>{formatDate(lead.lastSentAt)}</td>
                    <td className="lead-row-actions">
                      <button
                        className="lead-send-btn"
                        onClick={() => handleSendOne(lead._id)}
                        disabled={sendingId === lead._id || sendingBulk}
                        title={isSendingThisRow ? "กำลังส่ง..." : "ส่งอีเมลเชิญ"}
                      >
                        {isSendingThisRow ? (
                          <LoadingOutlined spin />
                        ) : (
                          <MailOutlined />
                        )}
                      </button>
                      <button
                        className="lead-delete-btn"
                        onClick={() => handleDelete(lead._id)}
                        title="ลบ"
                        disabled={isSendingThisRow}
                      >
                        <DeleteOutlined />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PartnerLeadAdmin;